import React from "react"

import { Clock, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  CoverageFilter,
  DAY_LABELS,
  DAY_NAMES,
  DAY_SHORT_LABELS,
  isCoverageFilterActive,
  NO_COVERAGE_FILTER,
} from "@/lib/collectionCoverage"
import { cn } from "@/lib/utils"
import { DayOfWeek } from "@/types/collections"

interface DirectoryCoverageFiltersProps {
  filter: CoverageFilter
  onChange: (filter: CoverageFilter) => void
  resultCount: number
  totalCount: number
}

const DirectoryCoverageFilters: React.FC<DirectoryCoverageFiltersProps> = ({
  filter,
  onChange,
  resultCount,
  totalCount,
}) => {
  const active = isCoverageFilterActive(filter)

  const toggleDay = (day: DayOfWeek) => {
    const days = filter.days.includes(day)
      ? filter.days.filter((d) => d !== day)
      : [...filter.days, day]
    onChange({ ...filter, days })
  }

  // An emptied time field means "no constraint", not midnight.
  const setTime = (key: "from" | "to", value: string) =>
    onChange({ ...filter, [key]: value === "" ? null : value })

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-palette-jungleGreen200 bg-white px-4 py-2.5">
      <div className="flex items-center gap-1">
        {DAY_NAMES.map((day) => {
          const selected = filter.days.includes(day)
          return (
            <button
              key={day}
              type="button"
              onClick={() => toggleDay(day)}
              aria-pressed={selected}
              aria-label={DAY_LABELS[day]}
              className={cn(
                "h-7 rounded-md border px-2.5 text-xs font-medium transition-colors",
                selected
                  ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                  : "border-palette-jungleGreen200 bg-white text-palette-jungleGreen600 hover:bg-palette-jungleGreen000",
              )}
            >
              {DAY_SHORT_LABELS[day]}
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-1.5">
        <Clock size={13} className="text-palette-jungleGreen400" />
        <Input
          type="time"
          aria-label="Collectable from"
          value={filter.from ?? ""}
          onChange={(e) => setTime("from", e.target.value)}
          className="h-7 w-[110px] border-palette-jungleGreen200 bg-white text-xs"
        />
        <span className="text-xs text-palette-jungleGreen400">to</span>
        <Input
          type="time"
          aria-label="Collectable until"
          value={filter.to ?? ""}
          onChange={(e) => setTime("to", e.target.value)}
          className="h-7 w-[110px] border-palette-jungleGreen200 bg-white text-xs"
        />
      </div>

      <div className="ml-auto flex items-center gap-2">
        <span className="text-xs text-palette-jungleGreen500">
          {active ? (
            <>
              <span className="font-medium text-palette-jungleGreen900">{resultCount}</span> of{" "}
              {totalCount} locations
            </>
          ) : (
            <>{totalCount} locations</>
          )}
        </span>
        {active && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(NO_COVERAGE_FILTER)}
            className="h-7 gap-1 px-2 text-xs text-palette-jungleGreen500 hover:text-palette-jungleGreen900"
          >
            <X size={12} />
            Clear
          </Button>
        )}
      </div>
    </div>
  )
}

export default DirectoryCoverageFilters
